import { useAccountStore } from "@/composables/account/useAccountStore";
import type { Account } from "@/types";

/**
 * The role the session acts with: the driving admin's while an admin manages a client account,
 * otherwise the account's own. `internal_role` alone is the MANAGED account's (`user`) during
 * impersonation, while the backend's access ceiling comes from the admin — so a gate reading it
 * denies an admin rights their own requests would be granted (FIX4-1 item 3).
 */
export function effectiveInternalRole(
  account: Account | null | undefined
): string | null {
  if (!account) return null;
  return account.admin_internal_role ?? account.internal_role ?? null;
}

/** The backend's access ladder, lowest first: `AccessClass` in `app/services/access_policy.py`. */
export type AccessClass =
  "PUBLIC" | "USER" | "SALES_READ" | "SALES_WRITE" | "SUPER";

const ACCESS_LADDER: readonly AccessClass[] = [
  "PUBLIC",
  "USER",
  "SALES_READ",
  "SALES_WRITE",
  "SUPER",
];

/** Mirrors `_ROLE_CEILING` in `app/services/access_policy.py`; any other role reaches PUBLIC only. */
const ROLE_CEILING: Readonly<Record<string, AccessClass>> = {
  super_admin: "SUPER",
  sales_admin: "SALES_WRITE",
  user: "USER",
};

/**
 * Whether the session's role reaches a route's access class, judged as the backend judges it: on
 * the effective role, so an admin managing a client keeps the admin's ceiling.
 *
 * Presentation only — the backend enforces the same ladder on every request.
 */
export function reachesAccessClass(
  account: Account | null | undefined,
  required: AccessClass
): boolean {
  const ceiling =
    ROLE_CEILING[effectiveInternalRole(account) ?? ""] ?? "PUBLIC";
  return ACCESS_LADDER.indexOf(ceiling) >= ACCESS_LADDER.indexOf(required);
}

/**
 * Canonical admin predicate: an internal admin/super_admin, or an admin-driven session
 * (admin_account_id set — impersonating or not). Single source of truth, so every gate and view
 * that shows admin-only UI agrees on who is an admin.
 */
export function isAdminAccount(account: Account | null | undefined): boolean {
  if (!account) return false;
  const role = effectiveInternalRole(account);
  return (
    typeof account.admin_account_id === "number" ||
    role === "admin" ||
    role === "super_admin" ||
    role === "sales_admin"
  );
}

/** Whether an admin drives this session on a client's account rather than working as themselves. */
export function managesClient(account: Account | null | undefined): boolean {
  return (
    typeof account?.admin_account_id === "number" &&
    account.admin_account_id !== account.id
  );
}

/**
 * The account for a gate: the store's cached `GET /api/account` (see useAccountStore) as the server
 * answered it — the "Show admin options" switch hides admin fields from the client pages, never from
 * a gate — or null when the load fails, even if an earlier page left an account behind, since a gate
 * must not open on an account it could not establish. Never rejects; the failure is logged.
 */
export async function loadAccountForGate(): Promise<Readonly<Account> | null> {
  const accountStore = useAccountStore();
  try {
    await accountStore.load();
    return accountStore.serverAccount;
  } catch (error) {
    console.error("Error fetching account data:", error);
    return null;
  }
}
